import { SUPPORT_CHAT_CONFIG } from '../config/supportChatConfig';
import { buildSocAiDeepLink } from './supportChatDeepLink';

export type VoicePlatform = 'ios' | 'android' | 'other';

export function detectVoicePlatform(): VoicePlatform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

export type VoiceShortcutKind = 'book' | 'track';

export function getHostedShortcutFileUrl(kind: VoiceShortcutKind, useNativeApp = false): string {
  const base = SUPPORT_CHAT_CONFIG.publicSiteUrl.replace(/\/$/, '');
  const files = SUPPORT_CHAT_CONFIG.siriShortcutFiles;
  const path = kind === 'track' ? files.trackWeb : useNativeApp ? files.bookApp : files.bookWeb;
  return `${base}${path}`;
}

/** Opens Apple Shortcuts to import a pre-built SOC shortcut (one tap → Add). */
export function openSiriShortcutInstaller(kind: VoiceShortcutKind = 'book', useNativeApp = false): boolean {
  if (typeof window === 'undefined') return false;
  const fileUrl = getHostedShortcutFileUrl(kind, useNativeApp);
  const importUrl = `shortcuts://import-shortcut?url=${encodeURIComponent(fileUrl)}`;
  window.location.href = importUrl;
  return true;
}

export function getAndroidInstallShortcutNote(): string {
  return 'Voice shortcuts are already on your SOC home-screen icon. Long-press the icon → Book service or Track orders.';
}

export function buildWebVoiceDeepLink(message: string): string {
  return buildSocAiDeepLink(
    SUPPORT_CHAT_CONFIG.publicSiteUrl,
    message,
    SUPPORT_CHAT_CONFIG.siriDefaultPath,
  );
}

export function buildNativeVoiceDeepLink(message: string): string {
  const encoded = encodeURIComponent(message.trim());
  return `${SUPPORT_CHAT_CONFIG.nativeAppScheme}://ai?socAi=${encoded}`;
}

/** Deep link for the current platform (web PWA vs native app install prompt). */
export function buildVoiceDeepLinkForPlatform(message: string, platform = detectVoicePlatform()): string {
  if (platform === 'ios' || platform === 'android') {
    return buildWebVoiceDeepLink(message);
  }
  return buildWebVoiceDeepLink(message);
}

export type VoiceSetupGuide = {
  platform: VoicePlatform;
  title: string;
  wakePhrase: string;
  steps: string[];
  deepLink: string;
  note?: string;
};

export function getVoiceSetupGuide(
  message = 'book a service',
  platform: VoicePlatform = detectVoicePlatform(),
): VoiceSetupGuide {
  const deepLink = buildWebVoiceDeepLink(message);

  if (platform === 'ios') {
    return {
      platform: 'ios',
      title: 'Siri — one tap setup',
      wakePhrase: '“Hey Siri, book a service” (after you tap Add to Siri)',
      deepLink,
      steps: [
        'Tap **Install Siri shortcut** below — Shortcuts opens automatically.',
        'Tap **Add Shortcut** in the Shortcuts app.',
        'Open the shortcut → **Add to Siri** → say “book a service”.',
        'Done — SOC opens with your booking message.',
      ],
      note: 'The link https://…/home?socAi=book%20a%20service is pre-filled. No copy-paste needed.',
    };
  }

  if (platform === 'android') {
    return {
      platform: 'android',
      title: 'Google Assistant — ready on install',
      wakePhrase: 'Long-press SOC icon → Book service (AI)',
      deepLink,
      steps: [
        'SOC voice shortcuts were added when you installed the app.',
        'Long-press the **SOC** icon on your home screen.',
        'Tap **Book service (AI)** or **Track orders (AI)**.',
        'Optional: Google Home → Routine → voice command → use the link below.',
      ],
      note: getAndroidInstallShortcutNote(),
    };
  }

  return {
    platform: 'other',
    title: 'Voice assistant setup',
    wakePhrase: 'Use Siri (iPhone) or Google Assistant (Android)',
    deepLink,
    steps: [
      'iPhone: Shortcuts app → Open URL → Add to Siri.',
      'Android: Google Home → Routine → Voice command → Open website.',
      'Paste the same SOC link in either platform.',
    ],
  };
}

export const VOICE_QUICK_MESSAGES = [
  { label: 'Book a service', message: 'book a service' },
  { label: 'Track orders', message: 'track my orders' },
  { label: 'Cleaning price', message: 'cleaning price' },
] as const;

export const VOICE_SETUP_DONE_KEY = 'soc_voice_assistant_setup_v1_done';
export const PWA_INSTALLED_KEY = 'soc_pwa_installed_v1';

export function isVoiceSetupDone(): boolean {
  try {
    return localStorage.getItem(VOICE_SETUP_DONE_KEY) === '1';
  } catch {
    return false;
  }
}

export function markVoiceSetupDone(): void {
  try {
    localStorage.setItem(VOICE_SETUP_DONE_KEY, '1');
  } catch {
    // ignore
  }
}

export function markPwaInstalled(): void {
  try {
    localStorage.setItem(PWA_INSTALLED_KEY, '1');
  } catch {
    // ignore
  }
}

export function isInstalledWebApp(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  let installedFlag = false;
  try {
    installedFlag = localStorage.getItem(PWA_INSTALLED_KEY) === '1';
  } catch {
    installedFlag = false;
  }
  return standalone || iosStandalone || installedFlag;
}

/** Show voice setup once — only when SOC is installed as PWA / home-screen app. */
export function shouldShowVoiceSetupOnInstall(): boolean {
  if (isVoiceSetupDone()) return false;
  return isInstalledWebApp();
}
